/* eslint-disable */
/*
 * SmartLead — Last Visited Product tracker (storefront drop-in).
 *
 * Drop this file into your Shopify theme's `assets/` folder and load it
 * site-wide from `theme.liquid` BEFORE any SmartLead form-submission code:
 *
 *   {{ 'smartlead-last-visited.js' | asset_url | script_tag }}
 *
 * Then on the PDP (product template), record the currently viewed product:
 *
 *   <script>
 *     window.SmartLead && window.SmartLead.lastVisited.set({
 *       sku:         {{ product.selected_or_first_available_variant.sku | json }},
 *       productUrl:  {{ shop.url | append: product.url | json }},
 *       title:       {{ product.title | json }},
 *       product:     {{ product.title | json }},
 *       productType: {{ product.type | json }},
 *       price:       {{ product.selected_or_first_available_variant.price | money_without_currency | json }},
 *       image:       {{ product.selected_or_first_available_variant.image
 *                       | default: product.featured_image
 *                       | image_url: width: 600 | json }}
 *     });
 *   </script>
 *
 * Then either:
 *
 *   (a) For JS-driven forms, call `attachToPayload(payload)` before POSTing:
 *
 *       const payload = window.SmartLead.lastVisited.attachToPayload({
 *         email, phone, formSource: "contact",
 *       });
 *       fetch("/api/leads/create", {
 *         method: "POST",
 *         headers: { "Content-Type": "application/json" },
 *         body: JSON.stringify(payload),
 *       });
 *
 *   (b) For plain HTML forms, add `data-smartlead-form` on the <form>; this
 *       script will inject hidden inputs (`lastVisitedSKU`, `lastVisitedProductUrl`, …)
 *       just before submit. Note this only works for forms that POST to your
 *       own SmartLead-aware backend (the public `/api/leads/create` endpoint
 *       accepts JSON, not form-encoded — wire the form to your handler that
 *       parses both, or use option (a)).
 *
 * Configuration (optional):
 *
 *   window.SmartLead.lastVisited.setTtlMinutes(30); // default
 *
 * Explicit user input always wins: any `lastVisited*` field already present in
 * the payload (e.g. user-entered wishlist SKU) is NOT overwritten by this
 * tracker.
 */
(function (global) {
  if (!global || !global.document) return;

  var STORAGE_KEY = "smartlead:lastVisited";
  var DEFAULT_TTL_MS = 30 * 60 * 1000;
  var ttlMs = DEFAULT_TTL_MS;

  function nowMs() {
    return Date.now();
  }

  function safeSessionStorage() {
    try {
      var s = global.sessionStorage;
      if (!s) return null;
      var probe = "__smartlead_probe__";
      s.setItem(probe, "1");
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }

  var storage = safeSessionStorage();

  function readRaw() {
    if (!storage) return null;
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function isExpired(rec) {
    if (!rec || typeof rec.viewedAt !== "number") return true;
    return nowMs() - rec.viewedAt > ttlMs;
  }

  function get() {
    var rec = readRaw();
    if (!rec || isExpired(rec)) return null;
    return rec;
  }

  function clear() {
    if (!storage) return;
    try {
      storage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }

  function trim(v) {
    if (v == null) return null;
    var s = String(v).trim();
    return s ? s : null;
  }

  function set(data) {
    if (!storage || !data || typeof data !== "object") return false;

    var rec = {
      sku: trim(data.sku),
      productUrl: trim(data.productUrl),
      title: trim(data.title),
      image: trim(data.image),
      product: trim(data.product) || trim(data.title),
      productType: trim(data.productType),
      price: trim(data.price),
      viewedAt: nowMs(),
    };

    // Need at least one identifier worth tracking.
    if (!rec.sku && !rec.productUrl) return false;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(rec));
      return true;
    } catch (e) {
      return false;
    }
  }

  function setTtlMinutes(minutes) {
    var n = Number(minutes);
    if (!isFinite(n) || n <= 0) return;
    ttlMs = n * 60 * 1000;
  }

  function attachToPayload(payload) {
    // Public API — must never throw. Returns the payload unchanged on any
    // unexpected error so callers can rely on the result.
    try {
      var p = payload && typeof payload === "object" ? payload : {};
      var rec = get();
      if (!rec) return p;

      if (!p.lastVisitedSKU && rec.sku) p.lastVisitedSKU = rec.sku;
      if (!p.lastVisitedProduct && rec.product) p.lastVisitedProduct = rec.product;
      if (!p.lastVisitedProductUrl && rec.productUrl) p.lastVisitedProductUrl = rec.productUrl;
      if (!p.lastVisitedTitle && rec.title) p.lastVisitedTitle = rec.title;
      if (!p.lastVisitedImage && rec.image) p.lastVisitedImage = rec.image;
      if (!p.lastVisitedPrice && rec.price) p.lastVisitedPrice = rec.price;
      if (!p.productType && rec.productType) p.productType = rec.productType;
      return p;
    } catch (e) {
      return payload;
    }
  }

  function fillFormHiddenInputs(form) {
    // Public API — must never throw.
    try {
      if (!form || form.nodeName !== "FORM") return;
      var rec = get();
      if (!rec) return;

      var fieldMap = {
        lastVisitedSKU: rec.sku,
        lastVisitedProduct: rec.product,
        lastVisitedProductUrl: rec.productUrl,
        lastVisitedTitle: rec.title,
        lastVisitedImage: rec.image,
        lastVisitedPrice: rec.price,
        productType: rec.productType,
      };

      Object.keys(fieldMap).forEach(function (name) {
        var value = fieldMap[name];
        if (!value) return;
        // Don't overwrite user-provided values.
        var existing = form.querySelector('[name="' + name + '"]');
        if (existing && trim(existing.value)) return;

        if (existing) {
          existing.value = value;
        } else {
          var input = global.document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          form.appendChild(input);
        }
      });
    } catch (e) {
      // ignore
    }
  }

  function captureFromShopifyAnalytics() {
    try {
      var meta = global.ShopifyAnalytics && global.ShopifyAnalytics.meta;
      if (!meta) return false;
      var page = meta.page || {};
      // Only auto-capture on real product pages.
      if (page.pageType && page.pageType !== "product") return false;
      var product = meta.product;
      if (!product) return false;

      var variant = (product.variants && product.variants[0]) || null;
      var price = variant && typeof variant.price === "number"
        ? (variant.price / 100).toFixed(2)
        : null;

      var location = global.location;
      var url = location ? location.origin + location.pathname : null;

      return set({
        sku: variant ? variant.sku : null,
        productUrl: url,
        title: product.title || (variant && variant.name) || null,
        product: product.title || null,
        productType: product.type || null,
        price: price,
        image: null,
      });
    } catch (e) {
      return false;
    }
  }

  function bindAutoForms() {
    if (!global.document.body) return;
    global.document.addEventListener(
      "submit",
      function (evt) {
        var form = evt.target;
        if (!form || form.nodeName !== "FORM") return;
        if (!form.hasAttribute("data-smartlead-form")) return;
        try {
          fillFormHiddenInputs(form);
        } catch (e) {
          // ignore
        }
      },
      true,
    );
  }

  var api = {
    get: get,
    set: set,
    clear: clear,
    setTtlMinutes: setTtlMinutes,
    attachToPayload: attachToPayload,
    fillFormHiddenInputs: fillFormHiddenInputs,
    captureFromShopifyAnalytics: captureFromShopifyAnalytics,
  };

  global.SmartLead = global.SmartLead || {};
  global.SmartLead.lastVisited = api;

  function onReady() {
    bindAutoForms();
    // If the PDP didn't call `set()` directly, try ShopifyAnalytics as a
    // best-effort fallback. This is opportunistic — the dedicated Liquid
    // snippet shown above is recommended.
    if (!get()) captureFromShopifyAnalytics();
  }

  if (global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})(typeof window !== "undefined" ? window : this);
